/**
 * Custom hook pour gérer l'état avec vérification automatique du montage
 * 
 * Ce hook remplace useState et empêche les mises à jour d'état sur des composants démontés,
 * évitant ainsi l'erreur "Can't perform a React state update on an unmounted component"
 * et l'erreur "Failed to execute 'removeChild' on 'Node'".
 * 
 * Usage:
 * const [value, setValue] = useSafeState(initialValue);
 * 
 * // setValue vérifiera automatiquement si le composant est monté avant de mettre à jour
 */

import { useState, useEffect, useRef, useCallback } from 'react';

export const useSafeState = (initialValue) => {
  const [state, setState] = useState(initialValue);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const setSafeState = useCallback((value) => {
    if (isMountedRef.current) {
      setState(value);
    }
  }, []);

  return [state, setSafeState, isMountedRef];
};

/**
 * Hook pour gérer plusieurs états avec vérification automatique
 * 
 * Usage:
 * const safeSetters = useSafeSetters();
 * const [value1, setValue1] = useState(initial1);
 * const [value2, setValue2] = useState(initial2);
 * 
 * // Utiliser les setters sécurisés
 * safeSetters.set(setValue1, newValue1);
 * safeSetters.set(setValue2, newValue2);
 */
export const useSafeSetters = () => {
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const safeSet = useCallback((setter, value) => {
    if (isMountedRef.current) {
      setter(value);
    }
  }, []);

  const safeSetMultiple = useCallback((updates) => {
    if (isMountedRef.current) {
      Object.entries(updates).forEach(([setter, value]) => {
        setter(value);
      });
    }
  }, []);

  return {
    set: safeSet,
    setMultiple: safeSetMultiple,
    isMounted: isMountedRef
  };
};

/**
 * Hook pour obtenir une référence isMounted partagée
 * 
 * Usage:
 * const isMountedRef = useIsMounted();
 * 
 * // Dans une fonction async
 * if (isMountedRef.current) {
 *   setState(newValue);
 * }
 */
export const useIsMounted = () => {
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return isMountedRef;
};

export default useSafeState;
